import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const TypicalWeekendPage = () => {
  const weekendPackages = [
    {
      id: 'camping-diving-1night',
      title: 'Weekend Dive & Camp (1 Night)',
      duration: '1 day / 1 night',
      price: '$295',
      priceNum: 295,
      image: 'https://images.pexels.com/photos/3098971/pexels-photo-3098971.jpeg',
      description: 'Perfect weekend escape combining camping and diving!'
    },
    {
      id: 'camping-diving-2day',
      title: 'Extended Dive & Camp (2 Days)',
      duration: '2 days / 1 night',
      price: '$395',
      priceNum: 395,
      image: 'https://images.unsplash.com/photo-1598644656788-c77be17dfc56',
      description: 'Ultimate adventure combining the best of land and sea!'
    },
    {
      id: 'camping-only-2day',
      title: 'Land-based Weekend (2 Days)',
      duration: '2 days / 1 night',
      price: '$245',
      priceNum: 245,
      image: 'https://images.pexels.com/photos/4909070/pexels-photo-4909070.jpeg',
      description: 'Pure land-based adventure with coastal camping and hiking'
    }
  ];

  const itinerary = [
    {
      day: 'Day 1',
      title: 'Arrival & Connection',
      activities: [
        {
          time: '6:00 AM',
          title: 'Sydney Departure',
          description: 'Begin your adventure with early morning pickup from Sydney - watch the city wake up as we head to paradise'
        },
        {
          time: '8:00 AM',
          title: 'Scenic Coastal Drive',
          description: 'Enjoy breathtaking coastal views during our 2-hour journey to pristine diving locations'
        },
        {
          time: '10:00 AM',
          title: 'Mindful Camp Setup',
          description: 'Set up your oceanfront camp together while enjoying a hearty breakfast with stunning water views'
        },
        {
          time: '11:30 AM',
          title: 'Welcome Circle & Nature Walk',
          description: 'Connect with fellow adventurers and explore the surrounding natural beauty through guided hiking'
        },
        {
          time: '2:00 PM',
          title: 'First Underwater Adventure',
          description: 'Dive into crystal-clear waters for your first guided underwater exploration of marine life'
        },
        {
          time: '6:00 PM',
          title: 'Sunset BBQ & Stargazing',
          description: 'Enjoy fresh BBQ dinner by the campfire, optional night walk, and spectacular stargazing under pristine skies'
        },
        {
          time: '9:00 PM',
          title: 'Evening Reflection',
          description: 'Share stories and connect with nature through mindful reflection around the warm campfire'
        }
      ]
    },
    {
      day: 'Day 2', 
      title: 'Deepening Experience',
      activities: [
        {
          time: '6:30 AM',
          title: 'Sunrise Beach Meditation',
          description: 'Wake to spectacular ocean sunrise with guided meditation and mindful breakfast by the water'
        },
        {
          time: '8:30 AM',
          title: 'Morning Dive Adventure',
          description: 'Experience deeper underwater exploration with enhanced visibility and abundant marine life'
        },
        {
          time: '11:00 AM',
          title: 'Underwater Photography',
          description: 'Capture magical underwater moments with professional photography guidance and techniques'
        },
        {
          time: '1:00 PM',
          title: 'Nourishing Lunch & Final Dive',
          description: 'Refuel with fresh lunch before your final underwater journey to hidden marine treasures'
        },
        {
          time: '3:30 PM',
          title: 'Mindful Camp Closure',
          description: 'Pack camp with gratitude while reflecting on transformative experiences and new friendships'
        },
        {
          time: '4:30 PM',
          title: 'Beach Walk & Connection',
          description: 'Final nature connection through peaceful beach walk and sharing of adventure memories'
        },
        {
          time: '6:00 PM',
          title: 'Return to Sydney',
          description: 'Comfortable journey back to Sydney, arriving by evening with unforgettable memories and renewed spirit'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20">
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1441974231531-c6227db76b6e)` }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative z-10 h-full flex items-center justify-center text-center">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">Typical Weekend Experience</h1>
              <p className="text-base sm:text-lg text-white/90">
                Immersive weekend adventures combining nature connection with transformative experiences
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Weekend Packages */}
      <section className="py-10 sm:py-14" style={{ backgroundColor: '#0b4c71' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Choose Your Weekend Adventure</h2>
            <p className="text-base sm:text-lg text-white/90 max-w-3xl mx-auto">
              Three carefully designed packages to suit different preferences
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-10">
            {weekendPackages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative h-32 sm:h-40 overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 left-3">
                    <span className="bg-coral-500 text-white px-2 py-1 rounded-full text-xs">
                      {pkg.duration}
                    </span>
                  </div>
                </div>
                
                <div className="p-4">
                  <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-1">{pkg.title}</h3>
                  <p className="text-coral-600 font-bold text-base mb-2">{pkg.price} per person</p>
                  <p className="text-slate-600 text-sm mb-4">{pkg.description}</p>
                  
                  <div className="flex space-x-2">
                    <Link
                      to={`/adventures/${pkg.id}`}
                      className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-800 py-2 rounded-lg font-semibold text-center transition-all text-sm"
                    >
                      Learn More
                    </Link>
                    <Link
                      to="/book"
                      className="flex-1 bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 text-white py-2 rounded-lg font-semibold text-center transition-all text-sm"
                    >
                      Book Now
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Itinerary */}
      <section className="py-12 sm:py-16 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1559827260-dc66d52bef19)` }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">A Typical Weekend Journey</h2>
            <p className="text-base sm:text-lg text-white/90 max-w-3xl mx-auto mb-2">
              Each adventure follows a mindful rhythm, designed to create deep connections with nature.
            </p>
            <p className="text-coral-400 italic text-sm">
              *Example from 2-day Camping & Diving adventure
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {itinerary.map((day, index) => (
              <motion.div
                key={day.day}
                initial={{ opacity: 0, x: index === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm hover:bg-white/90 p-4 sm:p-6 rounded-xl shadow-xl transition-all duration-300 group"
              >
                <div className="flex items-center mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold mr-3 ${
                    index === 0 ? 'bg-orange-500' : 'bg-blue-500'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white group-hover:text-slate-800 transition-colors">{day.day}</h3>
                    <p className="text-coral-300 group-hover:text-coral-600 font-semibold text-sm transition-colors">{day.title}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {day.activities.map((activity, idx) => (
                    <div key={idx} className="flex items-start">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 text-white text-xs font-bold ${
                        index === 0 ? 'bg-orange-500' : 'bg-blue-500'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                          <span className="text-coral-300 group-hover:text-coral-600 font-bold text-xs transition-colors">
                            {activity.time}
                          </span>
                          <h4 className="text-white group-hover:text-slate-800 font-semibold text-sm transition-colors">
                            {activity.title}
                          </h4>
                        </div>
                        <p className="text-white/90 group-hover:text-slate-600 transition-colors text-xs leading-relaxed">
                          {activity.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="text-center mt-8 sm:mt-10">
            <p className="text-white/90 mb-4 text-sm sm:text-base">
              Want to see specific itineraries for other adventures?
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/adventures/camping-diving-2day"
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full text-sm sm:text-base font-semibold transition-all transform hover:scale-105 shadow-xl"
              >
                See Full 2-Day Trip Details
              </Link>
              <Link
                to="/adventures"
                className="bg-slate-700 hover:bg-slate-800 text-white px-6 py-3 rounded-full text-sm sm:text-base font-semibold transition-all transform hover:scale-105 shadow-xl"
              >
                Explore All Adventures
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">Everything You Need Is Included</h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto">
              Just bring yourself and your sense of adventure - we've got everything else covered
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
              {
                icon: '🏕️',
                title: 'Complete Camping Setup',
                items: ['Tent & sleeping equipment', 'Cooking gear & utensils', 'Chairs & tables', 'Lighting & power']
              },
              {
                icon: '🤿',
                title: 'All Diving Equipment',
                items: ['Wetsuit & diving gear', 'Air tanks & weights', 'Masks, fins & snorkels', 'Safety equipment']
              },
              {
                icon: '🍽️',
                title: 'Meals & Refreshments',
                items: ['All meals provided', 'Fresh ingredients', 'Drinking water', 'Hot beverages & snacks']
              },
              {
                icon: '🚐',
                title: 'Transport & Guidance',
                items: ['Return transport from Sydney', 'MSDT certified instructor', 'First aid certified', 'Local knowledge & stories']
              }
            ].map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-3 sm:p-4 rounded-xl hover:bg-gradient-to-b hover:from-coral-50 hover:to-orange-50 transition-all duration-300"
              >
                <div className="text-3xl sm:text-4xl mb-2">{category.icon}</div>
                <h3 className="text-sm sm:text-base font-bold text-slate-800 mb-2">{category.title}</h3>
                <ul className="space-y-1">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="text-slate-600 text-xs">{item}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Perfect For Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-ocean-900 via-ocean-800 to-ocean-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">Perfect For</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
            {[
              {
                title: 'Couples & Partners',
                description: 'Reconnect with each other and nature in a peaceful, intimate setting'
              },
              {
                title: 'Solo Adventurers',
                description: 'Join a small, welcoming group and make meaningful connections'
              },
              {
                title: 'Small Friend Groups',
                description: 'Share an unforgettable experience with your closest friends'
              }
            ].map((group, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm p-4 sm:p-5 rounded-xl"
              >
                <h3 className="text-base sm:text-lg font-bold mb-2">{group.title}</h3>
                <p className="text-white/90 text-sm">{group.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/book"
              className="bg-coral-500 hover:bg-coral-600 text-white px-6 py-3 rounded-full text-base font-semibold transition-all transform hover:scale-105 shadow-xl"
            >
              Book Your Weekend Adventure
            </Link>
            <Link
              to="/contact"
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border-2 border-white/30 hover:border-white/50 px-6 py-3 rounded-full text-base font-semibold transition-all transform hover:scale-105"
            >
              Ask Questions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TypicalWeekendPage;